import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { MyLBApiConfiguration } from '../../../api/mylb/mylb-api.configuration';
import { HttpService } from '../../../api/shared/http-service.class';
import { IIntegrationDetails } from '../interfaces/iintegration-details.interface';
import { IApplicationTypeInfo } from '../interfaces/iapplication-type-info.interface';
import { IUpdateIntegrationValueRequest } from '../interfaces/iupdate-integration-value-request.interface';

/** IntegrationEntity names the integration API binds from the route. */
export type IntegrationEntityName = 'User' | 'Enrollment';

@Injectable({
	providedIn: 'root'
})
export class IntegrationService extends HttpService {
	constructor(config: MyLBApiConfiguration) {
		super(config, 'integration');
	}

	/**
	 * Get all integration details for a specific GUID
	 */
	getByGuid(guid: string, options?: { includeMetadata?: boolean }): Observable<IIntegrationDetails> {
		const includeMetadata = options?.includeMetadata === true;
		const params = includeMetadata ? { includeMetaData: includeMetadata } : undefined;
		return this.get<IIntegrationDetails>(`${this.serviceUrl}/${guid}`, { params });
	}

	/**
	 * [ADMIN ONLY] Get all integration details for a MyLanguageBird entity by its own ID (UserID, EnrollmentID).
	 * The backend resolves the correlation GUID in the Integration hub, so this never depends on a GUID copy
	 * held on the MyLB row (Core.Users.UserGUID), which can be stale. 404 when the entity has no hub mapping.
	 */
	getByEntityId(entityType: IntegrationEntityName, id: number, options?: { includeMetadata?: boolean }): Observable<IIntegrationDetails> {
		const includeMetadata = options?.includeMetadata === true;
		const params = includeMetadata ? { includeMetaData: includeMetadata } : undefined;
		return this.get<IIntegrationDetails>(`${this.serviceUrl}/mylb/${entityType}/${id}`, { params });
	}

	/**
	 * [ADMIN ONLY] Get available applications for a specific entity type
	 */
	getApplicationsByEntity(entityType: number): Observable<IApplicationTypeInfo[]> {
		return this.get<IApplicationTypeInfo[]>(`${this.serviceUrl}/entity/${entityType}/applications`);
	}

	/**
	 * [ADMIN ONLY] Update an existing integration value
	 */
	updateIntegrationValue(request: IUpdateIntegrationValueRequest): Observable<any> {
		return this.put<any>(`${this.serviceUrl}/update`, request);
	}

	/**
	 * [ADMIN ONLY] Create a new integration value
	 */
	createIntegrationValue(request: IUpdateIntegrationValueRequest): Observable<any> {
		return this.post<any>(`${this.serviceUrl}/create`, request);
	}
}
